import React, { useState, useContext } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import { useMutation, useQueryClient } from 'react-query'
import { apiPost, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import useMunicipalityFeatures from 'api/hooks/use-municipality-features'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFlask } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'

function AddAttribute({ reportId, reportFeatures }) {
  const [show, setShow] = useState(false)
  const [featureToDelete, setFeatureToDelete] = useState(null)
  const [confirmText, setConfirmText] = useState('')
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  const { municipality } = useContext(UserContext)
  const navigate = useNavigate()

  const { isSuccess: featuresSuccess, data: availableFeatures } =
    useMunicipalityFeatures({})

  const invalidate = () => {
    setTimeout(() => {
      queryClient.invalidateQueries([`reports/${reportId}`])
      queryClient.invalidateQueries(['transactions'])
    }, 500)
  }

  const createMutation = useMutation(
    (municipalityFeatureId) =>
      apiPost({
        url: `audit_reports/${reportId}/features`,
        token,
        objectName: 'report_feature',
        params: {
          audit_report_id: reportId,
          municipality_feature_id: municipalityFeatureId
        }
      }),
    { onSuccess: invalidate }
  )

  const deleteMutation = useMutation(
    (reportFeatureId) => apiDelete({ url: `audit_reports/${reportId}/features/${reportFeatureId}`, token }),
    {
      onSuccess: () => {
        setFeatureToDelete(null)
        setConfirmText('')
        invalidate()
      }
    }
  )

  const isSelected = (municipalityFeature) =>
    (reportFeatures || []).some((f) => f.municipality_feature_id === municipalityFeature.id)

  const handleToggle = (municipalityFeature) => {
    if (isSelected(municipalityFeature)) {
      const rf = (reportFeatures || []).find(
        (f) => f.municipality_feature_id === municipalityFeature.id
      )
      if (rf) {
        if (rf.total_count > 0) {
          setFeatureToDelete(rf)
        } else {
          deleteMutation.mutate(rf.id)
        }
      }
    } else {
      createMutation.mutate(municipalityFeature.id)
    }
  }

  const handleConfirmDelete = () => {
    if (featureToDelete) {
      deleteMutation.mutate(featureToDelete.id)
    }
  }

  const handleCancelDelete = () => {
    setFeatureToDelete(null)
    setConfirmText('')
  }

  return (
    <>
      <Button
        variant="light"
        size="sm"
        className="me-2"
        onClick={() => setShow(true)}
      >
        <FontAwesomeIcon icon={faFlask} className="me-1" />
        Attributes
      </Button>

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage Testing Attributes</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '400px' }}>
          <div className="text-muted small mb-3">
            Attributes are configured in{' '}
            <a
              href="/settings"
              onClick={(e) => {
                e.preventDefault()
                navigate('/settings')
              }}
            >
              Settings
            </a>
            .
          </div>

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {featuresSuccess && availableFeatures.length > 0
              ? availableFeatures.map((feature) => (
                  <div
                    key={feature.id}
                    className="d-flex align-items-start mb-2 p-2 border rounded bg-white"
                  >
                    <Form.Check
                      type="checkbox"
                      id={`attribute-${feature.id}`}
                      checked={isSelected(feature)}
                      onChange={() => handleToggle(feature)}
                      className="me-3 mt-1"
                    />
                    <label
                      htmlFor={`attribute-${feature.id}`}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="fw-medium">
                        {municipality.abbreviation}-{feature.key}: {feature.name}
                      </span>
                      {feature.description && (
                        <div className="text-muted small mt-1">
                          {feature.description}
                        </div>
                      )}
                    </label>
                  </div>
                ))
              : featuresSuccess && (
                  <div className="text-center py-4">
                    <div className="text-muted mb-2">No attributes available</div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate('/settings')}
                    >
                      Create Attributes in Settings
                    </Button>
                  </div>
                )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={() => setShow(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!featureToDelete} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Testing Data?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Removing the attribute <strong>{featureToDelete?.name}</strong> will
            permanently delete <strong>{featureToDelete?.total_count}</strong>{' '}
            associated test{featureToDelete?.total_count === 1 ? '' : 's'}. This
            action cannot be undone.
          </p>
          <Form.Group>
            <Form.Label>
              Type <strong>I understand</strong> to confirm:
            </Form.Label>
            <Form.Control
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="I understand"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={confirmText !== 'I understand'}
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AddAttribute
